// Comms email pass (1.12.212-beta): the reply box when Email is the chosen type.
//
// A taller box with a small formatting bar along its bottom: Bold, Italic, Underline, bulleted list, numbered list, link, clear
// formatting. Keyboard: Ctrl/⌘+B / I / U, Ctrl/⌘+Shift+8 (bullets), Ctrl/⌘+Shift+7 (numbers), Ctrl/⌘+K (link), Ctrl/⌘+\ (clear).
// The bar is an ARIA toolbar: one Tab stop, Left / Right / Home / End move along it, every button is labelled, and the toggles
// say whether they are on (aria-pressed).
//
// It is a plain contentEditable (no editor library), and what it holds is only ever trusted after sanitizeEmailHtml (src/lib/
// emailHtml.js): the composer sanitizes on every change it sends upward, the server sanitizes again on send, and the bubble
// sanitizes again on display. Pasting drops the source's formatting (plain text only), so nothing foreign is ever pasted in.
import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { escapeHtml, safeLinkHref, sanitizeEmailHtml } from '../../lib/emailHtml';

export const EMAIL_EDITOR_MIN_HEIGHT = 80;
export const EMAIL_EDITOR_MAX_HEIGHT = 360;

export const FORMAT_BUTTONS = [
    { key: 'bold', label: 'Bold', glyph: 'B', command: 'bold', shortcut: 'Ctrl+B', toggle: true, style: { fontWeight: 800 } },
    { key: 'italic', label: 'Italic', glyph: 'I', command: 'italic', shortcut: 'Ctrl+I', toggle: true, style: { fontStyle: 'italic' } },
    { key: 'underline', label: 'Underline', glyph: 'U', command: 'underline', shortcut: 'Ctrl+U', toggle: true, style: { textDecoration: 'underline' } },
    { key: 'bullets', label: 'Bulleted list', glyph: '•≡', command: 'insertUnorderedList', shortcut: 'Ctrl+Shift+8', toggle: true },
    { key: 'numbers', label: 'Numbered list', glyph: '1≡', command: 'insertOrderedList', shortcut: 'Ctrl+Shift+7', toggle: true },
    { key: 'link', label: 'Link', glyph: '🔗', command: 'createLink', shortcut: 'Ctrl+K' },
    { key: 'clear', label: 'Clear formatting', glyph: 'T̸', command: 'removeFormat', shortcut: 'Ctrl+\\' },
];

function runCommand(command, value) {
    if (typeof document === 'undefined' || typeof document.execCommand !== 'function') return false;
    try {
        return document.execCommand(command, false, value);
    } catch {
        return false;
    }
}

function commandState(command) {
    if (typeof document === 'undefined' || typeof document.queryCommandState !== 'function') return false;
    try {
        return Boolean(document.queryCommandState(command));
    } catch {
        return false;
    }
}

export default function EmailRichEditor({
    value = '',
    actions = null,
    onChange,
    onKeyDown,
    placeholder = 'Write your email…',
    ariaLabel = 'Email message',
    disabled = false,
    readOnly = false,
}) {
    const editorRef = useRef(null);
    const toolbarRef = useRef(null);
    const linkInputRef = useRef(null);
    const savedRangeRef = useRef(null);
    const lastEmittedRef = useRef(null);
    const [active, setActive] = useState({});
    const [focusIndex, setFocusIndex] = useState(0);
    const [linkOpen, setLinkOpen] = useState(false);
    const [linkValue, setLinkValue] = useState('https://');
    const [linkError, setLinkError] = useState('');
    const [empty, setEmpty] = useState(!value);
    const hintId = `email-editor-hint-${useId().replace(/:/g, '')}`;

    // Write the value in only when it changed from outside (a template, a reset, switching channels) — never while typing,
    // so the caret stays where the person put it.
    useEffect(() => {
        const node = editorRef.current;
        if (!node) return;
        if (value === lastEmittedRef.current) return;
        const safe = sanitizeEmailHtml(value);
        if (node.innerHTML !== safe) node.innerHTML = safe;
        lastEmittedRef.current = value;
        setEmpty(!node.textContent.trim());
    }, [value]);

    const emit = useCallback(() => {
        const node = editorRef.current;
        if (!node) return;
        const html = sanitizeEmailHtml(node.innerHTML);
        lastEmittedRef.current = html;
        setEmpty(!node.textContent.trim() && !node.querySelector('li'));
        onChange?.(html);
    }, [onChange]);

    const refreshActive = useCallback(() => {
        const node = editorRef.current;
        const selection = typeof window !== 'undefined' ? window.getSelection?.() : null;
        if (!node || !selection || !selection.anchorNode || !node.contains(selection.anchorNode)) return;
        setActive(Object.fromEntries(FORMAT_BUTTONS.filter((button) => button.toggle).map((button) => [button.key, commandState(button.command)])));
    }, []);

    useEffect(() => {
        if (typeof document === 'undefined') return undefined;
        document.addEventListener('selectionchange', refreshActive);
        return () => document.removeEventListener('selectionchange', refreshActive);
    }, [refreshActive]);

    const saveSelection = () => {
        const selection = window.getSelection?.();
        if (selection && selection.rangeCount && editorRef.current?.contains(selection.anchorNode)) {
            savedRangeRef.current = selection.getRangeAt(0).cloneRange();
        }
    };

    const restoreSelection = () => {
        editorRef.current?.focus();
        const range = savedRangeRef.current;
        const selection = window.getSelection?.();
        if (range && selection) {
            selection.removeAllRanges();
            selection.addRange(range);
        }
    };

    const openLink = () => {
        saveSelection();
        setLinkValue('https://');
        setLinkError('');
        setLinkOpen(true);
        setTimeout(() => linkInputRef.current?.focus(), 0);
    };

    const applyLink = () => {
        const href = safeLinkHref(linkValue.trim());
        if (!href) {
            setLinkError('Use a web address starting with https:// or http://, or mailto:');
            return;
        }
        restoreSelection();
        const selection = window.getSelection?.();
        if (!selection || selection.isCollapsed) {
            // Nothing selected: the address itself becomes the linked words.
            runCommand('insertHTML', `<a href="${escapeHtml(href)}">${escapeHtml(href)}</a>`);
        } else {
            runCommand('createLink', href);
        }
        setLinkOpen(false);
        emit();
    };

    const apply = (button) => {
        if (disabled || readOnly) return;
        if (button.key === 'link') {
            openLink();
            return;
        }
        editorRef.current?.focus();
        runCommand(button.command);
        if (button.key === 'clear') runCommand('unlink');
        emit();
        refreshActive();
    };

    const handleKeyDown = (event) => {
        const mod = event.ctrlKey || event.metaKey;
        if (mod && !event.altKey) {
            const key = event.key.toLowerCase();
            const byKey = {
                b: 'bold', i: 'italic', u: 'underline', k: 'link', '\\': 'clear',
                ...(event.shiftKey ? { '*': 'bullets', 8: 'bullets', '&': 'numbers', 7: 'numbers' } : {}),
            };
            const target = FORMAT_BUTTONS.find((button) => button.key === byKey[key]);
            if (target) {
                event.preventDefault();
                apply(target);
                return;
            }
        }
        onKeyDown?.(event);
    };

    const handlePaste = (event) => {
        // Formatting from elsewhere is dropped: the words come in, as plain text.
        const text = event.clipboardData?.getData('text/plain');
        if (typeof text !== 'string') return;
        event.preventDefault();
        runCommand('insertText', text);
        emit();
    };

    const handleToolbarKeyDown = (event) => {
        const count = FORMAT_BUTTONS.length;
        let next = null;
        if (event.key === 'ArrowRight') next = (focusIndex + 1) % count;
        if (event.key === 'ArrowLeft') next = (focusIndex - 1 + count) % count;
        if (event.key === 'Home') next = 0;
        if (event.key === 'End') next = count - 1;
        if (next === null) return;
        event.preventDefault();
        setFocusIndex(next);
        toolbarRef.current?.querySelectorAll('[data-format-button]')[next]?.focus();
    };

    return (
        <div className="ia-email-editor" data-email-editor="true">
            <div
                ref={editorRef}
                role="textbox"
                aria-multiline="true"
                aria-label={ariaLabel}
                aria-describedby={hintId}
                aria-disabled={disabled ? 'true' : undefined}
                aria-readonly={readOnly ? 'true' : undefined}
                contentEditable={!disabled && !readOnly}
                suppressContentEditableWarning
                data-placeholder={placeholder}
                data-empty={empty ? 'true' : 'false'}
                className="ia-email-editor__area ia-email-html"
                style={{ minHeight: EMAIL_EDITOR_MIN_HEIGHT, maxHeight: EMAIL_EDITOR_MAX_HEIGHT }}
                onInput={emit}
                onBlur={() => { saveSelection(); emit(); }}
                onKeyDown={handleKeyDown}
                onKeyUp={refreshActive}
                onMouseUp={refreshActive}
                onPaste={handlePaste}
                tabIndex={disabled ? -1 : 0}
            />
            <span id={hintId} className="sr-only">Formatting shortcuts: Ctrl or Command plus B, I, U, K for a link.</span>
            {linkOpen && (
                <div className="ia-email-editor__link" role="group" aria-label="Add a link" data-email-link-form="true">
                    <label className="flex min-w-0 flex-1 items-center gap-2 text-xs font-semibold">
                        <span>Link address</span>
                        <input
                            ref={linkInputRef}
                            type="url"
                            value={linkValue}
                            onChange={(event) => { setLinkValue(event.target.value); setLinkError(''); }}
                            onKeyDown={(event) => {
                                if (event.key === 'Enter') { event.preventDefault(); applyLink(); }
                                if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); setLinkOpen(false); restoreSelection(); }
                            }}
                            aria-invalid={linkError ? 'true' : undefined}
                            className="min-w-0 flex-1 rounded-lg border border-[var(--surface-border)] bg-transparent px-2 py-1 text-sm text-[var(--ink)]"
                        />
                    </label>
                    <button type="button" className="ia-btn3d ia-comms-pill ia-btn3d--go" onClick={applyLink}>Add link</button>
                    <button type="button" className="ia-btn3d ia-comms-pill" onClick={() => { setLinkOpen(false); restoreSelection(); }}>Cancel</button>
                    {linkError && <p role="alert" className="w-full text-xs text-[#FCA5A5]">{linkError}</p>}
                </div>
            )}
            <div
                ref={toolbarRef}
                role="toolbar"
                aria-label="Formatting"
                className="ia-email-editor__toolbar"
                data-email-toolbar="true"
                onKeyDown={handleToolbarKeyDown}
            >
                {FORMAT_BUTTONS.map((button, index) => (
                    <button
                        key={button.key}
                        type="button"
                        className="ia-btn3d ia-comms-pill ia-email-format-button ia-hover-pulse-off"
                        data-format-button={button.key}
                        aria-label={`${button.label} (${button.shortcut})`}
                        title={`${button.label} (${button.shortcut})`}
                        aria-pressed={button.toggle ? Boolean(active[button.key]) : undefined}
                        tabIndex={index === focusIndex ? 0 : -1}
                        disabled={disabled || readOnly}
                        // Keep the selection in the box while pressing a button.
                        onMouseDown={(event) => event.preventDefault()}
                        onFocus={() => setFocusIndex(index)}
                        onClick={() => apply(button)}
                    >
                        <span aria-hidden="true" style={button.style}>{button.glyph}</span>
                    </button>
                ))}
                {actions}
            </div>
        </div>
    );
}
